import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import styled, { type CSSProperties } from 'styled-components';

import { SlotMachineAnimatedNumber } from '@sorare/core/src/atoms/animations/SlotMachineAnimatedNumber';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Progress } from '@sorare/core/src/atoms/loader/Progress';
import { HeadlineL, LabelM, LabelS } from '@sorare/core/src/atoms/typography';
import { NeonEdge } from '@sorare/core/src/atoms/ui/NeonEdge';
import {
  ensureHexColor,
  getRgbColorFromHex,
} from '@sorare/core/src/lib/colors';

import type {
  CollectionAnimationHeader_cardCollection,
  CollectionAnimationHeader_competition,
} from './__generated__/index.graphql';

const HeaderWrapper = styled.div`
  flex-shrink: 0;
  background: linear-gradient(
      180deg,
      rgba(var(--rgb-color), 0.1) 0%,
      rgba(var(--c-rgb-black), 0.8) 30%
    ),
    var(--c-black);
  overflow: hidden;
  z-index: 1;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image: radial-gradient(
      circle,
      rgba(255, 255, 255, 0.1) 1px,
      transparent 1px
    );
    background-size: 8px 8px;
    opacity: 0.3;
    pointer-events: none;
  }
`;

const InnerWrapper = styled(Vertical)`
  padding: var(--double-unit) 0;
`;

const CompetitionLogo = styled.img`
  height: 56px;
  object-fit: contain;
`;

const SeasonText = styled(LabelM)`
  color: var(--c-white);
  opacity: 0.8;
  position: relative;
`;

const StyledProgress = styled(Progress)`
  flex: 1;
  --height: 10px;
  min-width: min(160px, calc(40 * var(--vw)));
`;

// we have difficulty including color into API Competition type, so it's hardcoded for now
const HARDCODED_COMPETITION_COLORS: Record<string, string> = {
  'premier-league-gb-eng': '#F258E3',
  'laliga-es': '#FF563D',
  'bundesliga-de': '#FF0033',
  'ligue-1-fr': '#0061FE',
  nba: '#FF0033',
};

type Props = {
  cardCollections: readonly CollectionAnimationHeader_cardCollection[];
  competition: CollectionAnimationHeader_competition;
  newCardSlugs: string[];
  animatingCardIndex: Nullable<number>;
};

export const CollectionAnimationHeader = ({
  cardCollections,
  competition,
  newCardSlugs,
  animatingCardIndex,
}: Props) => {
  const cardCollection = cardCollections[0];
  const { sportSeason } = cardCollection;
  const { startYear, endYear, singleCivilYear } = sportSeason || {};

  const totalSlots = cardCollections.reduce((total, collection) => {
    const { userCardCollection } = collection;
    return total + (userCardCollection?.slotsCount || 0);
  }, 0);

  const totalCollectedCount =
    cardCollections.reduce((acc, cur) => {
      const { userCardCollection } = cur;
      const slots = userCardCollection?.slots || [];
      const collected = slots.filter(slot => {
        return (
          !!slot.highlightedCardCollectionCard &&
          newCardSlugs.every(
            slug => slug !== slot.highlightedCardCollectionCard?.anyCard.slug
          )
        );
      }).length;
      return acc + collected;
    }, 0) +
    (typeof animatingCardIndex === 'number' ? animatingCardIndex + 1 : 0);

  const displayedStartYear = singleCivilYear
    ? startYear
    : startYear && startYear % 2000;
  const displayedEndYear = !singleCivilYear && endYear ? endYear % 2000 : null;

  const { displayName: competitionName, pictureUrl } = competition;

  const color =
    ensureHexColor(HARDCODED_COMPETITION_COLORS[competition.slug]) || '#4a148c';
  const rgbColor = getRgbColorFromHex(color);
  return (
    <HeaderWrapper
      style={
        {
          '--rgb-color': rgbColor,
        } as React.CSSProperties
      }
    >
      <NeonEdge color={color} side="bottom" spread="80%" showHalo="always">
        <InnerWrapper center>
          <Horizontal>
            {!!pictureUrl && (
              <CompetitionLogo src={pictureUrl} alt={competitionName} />
            )}
            <Vertical gap={0}>
              {!!displayedStartYear && (
                <SeasonText
                  bold
                  className="text-gradient-color"
                  style={
                    {
                      '--text-gradient-color': `linear-gradient(
                  45deg,
                  #f8d3da,
                  #b3a9f4 28%,
                  #fbe9fb 54%,
                  #4f94fd 100%
                )`,
                    } as CSSProperties
                  }
                >
                  {displayedStartYear}
                  {displayedEndYear ? ` - ${displayedEndYear}` : ''} Edition
                </SeasonText>
              )}
              <HeadlineL color="var(--c-white)">{competitionName}</HeadlineL>
            </Vertical>
          </Horizontal>

          <Horizontal>
            <StyledProgress
              value={totalCollectedCount}
              max={totalSlots}
              color="var(--c-yellow-400)"
              bgColor="rgba(var(--c-rgb-white), 0.05)"
              skipStartAnimation
            />
            <Horizontal gap={0.5} style={{ alignItems: 'flex-end' }}>
              <LabelM bold>
                <SlotMachineAnimatedNumber value={totalCollectedCount} />
              </LabelM>{' '}
              <LabelS color="var(--c-nd-600)">/ {totalSlots}</LabelS>
            </Horizontal>
          </Horizontal>
        </InnerWrapper>
      </NeonEdge>
    </HeaderWrapper>
  );
};

CollectionAnimationHeader.fragments = {
  cardCollection: gql`
    fragment CollectionAnimationHeader_cardCollection on CardCollection {
      slug
      sportSeason {
        name
        startYear
        endYear
        singleCivilYear
      }
      userCardCollection(forUserSlug: $user) {
        id
        slotsCount
        slots {
          slug
          cardCollectionCardsCount
          highlightedCardCollectionCard {
            id
            anyCard {
              slug
            }
          }
        }
      }
    }
  ` as TypedDocumentNode<CollectionAnimationHeader_cardCollection>,
  competition: gql`
    fragment CollectionAnimationHeader_competition on Competition {
      slug
      displayName
      pictureUrl
    }
  ` as TypedDocumentNode<CollectionAnimationHeader_competition>,
};
